import { captureException } from "@sentry/nextjs";
import { config } from "@lib/utils/config";
import { posthog } from "@src/analytics";
import { isIgnoredErrorMessage } from "./ignoredErrors";

/**
 * Normalizes an unknown error value into an Error object.
 * Handles strings, objects with message property, and other types.
 */
const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  if (error && typeof error === "object") {
    // Handle objects with a message property (common in many libraries)
    if ("message" in error && typeof error.message === "string") {
      const normalizedError = new Error(error.message);
      // Preserve additional properties on the error
      Object.assign(normalizedError, error);
      return normalizedError;
    }

    // Try to get constructor name for identification
    const name = error.constructor?.name;
    if (name && name !== "Object") {
      return new Error(`${name} error occurred`);
    }

    // Try toString if it's overridden
    const str = String(error);
    if (str !== "[object Object]") {
      return new Error(str);
    }

    return new Error("Unknown error (object with no message)");
  }

  return new Error(String(error));
};

export const reportError = (error: unknown): void => {
  const normalizedError = normalizeError(error);

  if (isIgnoredErrorMessage(normalizedError.message)) {
    return;
  }

  if (config.sentryEnabled) {
    captureException(normalizedError, {
      level: "error",
    });
  }
  
  if (config.posthogEnabled) {
    posthog.captureException(normalizedError)
  }
  
  if (!config.sentryEnabled && !config.posthogEnabled) {
    console.error(normalizedError);
  }
};
